import React, { useState } from "react"
import styled, { css } from "styled-components"
import PropTypes from "prop-types"

import { MfFontIcon } from "../../foundations/FontIcon"
import { MfTypo } from "../../foundations/Typo"

import { MfCell } from "../../components/Cell"
import { MfButton } from "../../components/Button"
import { MfProgressBar } from "../../components/ProgressBar"

const ContainerStyle = styled.div`
  border-radius: 8px;
  border: solid 1px ${({ theme }) => theme.color.border.lighter};
  width: 100%;
  margin-bottom: ${({ theme }) => theme.spacing(3)};
  overflow: hidden;
  display: block;
  ${({ $single }) => $single && `border: none;`}
`

/*
  Container styles to create an ellipsis on filenames that are too long for the container.
  The extra container is needed to be able to ellipsis inside a flex child. The FileName
  component composes the necessary container styles to handle the ellipsis.
*/
const NameContainer = styled.div`
  display: table;
  table-layout: fixed;
  width: 100%;
  padding-right: 30px;
`

const SubLabelContiner = styled.div`
  margin-right: 36px;
  display: flex;
  gap: 8px;
  flex-direction: column;
`

const FontIconStyled = styled(MfFontIcon)`
  color: ${({ theme }) => theme.color.text.lighter};
`

const TypoSubValue = styled(MfTypo).attrs({ variant: "bodytext2" })`
  color: ${({ theme }) => theme.color.text.darker};
`

const Ellipsis = styled(MfTypo).attrs({ variant: "bodytext1", weight: "semibold" })`
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`
const UploadedFile = styled.div`
  position: relative;
  width: 100%;
  padding: ${({ theme }) => `${theme.spacing(3)} 0 ${theme.spacing(3)} ${theme.spacing(2)}`};
  cursor: pointer;

  & + div {
    border-top: solid 1px ${({ theme }) => theme.color.border.lighter};
  }

  &:hover {
    background-color: ${({ theme }) => theme.color.hover.light};

    ${NameContainer} {
      text-decoration: underline;
    }
  }

  ${({ $single }) =>
    $single &&
    css`
      border: solid 1px ${({ theme }) => theme.color.border.lighter};
      border-radius: 8px;
      overflow: hidden;

      &:hover {
        border-radius: 8px;
        background-color: ${({ theme }) => theme.color.hover.light};
        border-color: ${({ theme }) => theme.color.accent.default};
        box-shadow:
          1px 1px 0 inset ${({ theme }) => theme.color.accent.default},
          -1px -1px 0 inset ${({ theme }) => theme.color.accent.default},
          1px -1px 0 inset ${({ theme }) => theme.color.accent.default},
          -1px 1px 0 inset ${({ theme }) => theme.color.accent.default};

        ${NameContainer} {
          text-decoration: underline;
        }
      }
    `}
`
const ButtonPositioning = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spaceValues[2]};
  margin-right: ${({ theme }) => theme.spaceValues[3]};
`

const CellContainer = styled.div`
  display: flex;
`

const ButtonStyles = styled(MfButton)`
  color: ${({ theme }) => theme.color.text.lighter};
  padding: 0;

  &:hover {
    color: ${({ theme }) => theme.color.status.error};
  }
`

export function FileList({
  files = undefined,
  onDelete = undefined,
  deletePopup: DeletePopup,
  ...props
}) {
  const [showPopup, setShowPopup] = useState(false)
  const [fileToDelete, setFileToDelete] = useState(null)

  const deleteFile = (event, file) => {
    event.stopPropagation()
    if (!DeletePopup) return deleteFileConfirm(file)
    setShowPopup(true)
    setFileToDelete(file)
  }

  const deleteFileConfirm = (file) => {
    setShowPopup(false)
    onDelete(file)
  }

  const handleFileDownload = (e, file) => {
    e.stopPropagation()
    if (file?.download) {
      file.download(file)
    } else {
      const url = URL.createObjectURL(file)

      if (file.type.startsWith("image/") || file.type === "application/pdf") {
        window.open(url)
      } else {
        const a = document.createElement("a")
        a.style.display = "none"
        a.href = url
        a.download = file.name
        document.body.appendChild(a)
        a.click()
        URL.revokeObjectURL(url)
      }
    }
  }

  function formatFileSize(file) {
    if (!file || file.error) {
      return file?.error || "Invalid file"
    }

    const { size } = file

    if (size < 1000) {
      return `${size} B`
    } else if (size < 1000 * 1024) {
      return `${(size / 1024).toFixed(2)} KB`
    } else {
      return `${(size / (1024 * 1024)).toFixed(2)} MB`
    }
  }

  return (
    <>
      <>
        {files && !!files.length && (
          <ContainerStyle {...props} $single={files.length === 1}>
            {files.map((file) => (
              <UploadedFile
                $single={files.length === 1}
                key={file?.id ? file.id : file.name}
                onClick={(e) => handleFileDownload(e, file)}
              >
                {files && file.progressUpload === 100 && file.name ? (
                  <CellContainer>
                    <MfCell
                      icon={
                        <MfFontIcon
                          icon={file.error ? "fonticons-warning" : "fonticons-check"}
                          color={file.error ? "#e93e2e" : "accent:default"}
                        />
                      }
                      value={
                        <NameContainer>
                          <Ellipsis>{file.name}</Ellipsis>
                        </NameContainer>
                      }
                      subValue={formatFileSize(file)}
                    />
                    {file && (
                      <ButtonPositioning>
                        {file.retry !== undefined && (
                          <ButtonStyles
                            title="retry file"
                            onClick={(e) => file?.retry(e, file)}
                            text
                          >
                            <FontIconStyled icon="fonticons-circular-arrow" />
                          </ButtonStyles>
                        )}
                        {(file.error === undefined || file.showDeleteOnError) && (
                          <ButtonStyles
                            title="delete file"
                            onClick={(e) => deleteFile(e, file)}
                            text
                          >
                            <FontIconStyled icon="fonticons-trashcan" />
                          </ButtonStyles>
                        )}
                      </ButtonPositioning>
                    )}
                  </CellContainer>
                ) : (
                  <CellContainer>
                    <MfCell
                      icon={<FontIconStyled icon="fonticons-file" />}
                      value={
                        <NameContainer>
                          <Ellipsis>
                            <div>{file.name}</div>
                          </Ellipsis>
                        </NameContainer>
                      }
                      subValue={
                        <SubLabelContiner>
                          <TypoSubValue>{file.progressLabel}</TypoSubValue>
                          <MfProgressBar size={file.progressUpload} />
                        </SubLabelContiner>
                      }
                    />
                    <ButtonPositioning>
                      <ButtonStyles title="delete file" onClick={(e) => deleteFile(e, file)} text>
                        <FontIconStyled icon="fonticons-trashcan" />
                      </ButtonStyles>
                    </ButtonPositioning>
                  </CellContainer>
                )}
              </UploadedFile>
            ))}
          </ContainerStyle>
        )}
        {DeletePopup && (
          <DeletePopup
            fileToDelete={fileToDelete}
            onConfirm={() => deleteFileConfirm(fileToDelete)}
            onClose={() => setShowPopup(false)}
            popupProps={{ show: showPopup, bottomSheet: true, onClose: () => setShowPopup(false) }}
          />
        )}
      </>
    </>
  )
}

FileList.propTypes = {
  files: PropTypes.array,
  onDelete: PropTypes.func,
  deletePopup: PropTypes.func,
}
